import {
  SEASON_IDS_PARAM,
  SELECTED_CROP_SEASONS_STORAGE_KEY
} from './cropSeasonUrl';

/**
 * Seed the crop-season selection from a `?seasonIds=` URL param before React
 * renders — the new-tab (Ctrl/Cmd+click) half of opening an application from the
 * product view.
 *
 * An application opened from the product-scoped Production Records view carries
 * its crop season(s) in the URL. The Applications list is season-scoped, and the
 * crop-season breadcrumb reads the selected seasons from localStorage at mount
 * — so writing them here (before `ReactDOM.render`) lets the breadcrumb
 * initialize to the application's season, and the season-scoped list can show
 * it, without touching the breadcrumb's own logic. The param is then stripped so
 * it doesn't linger or re-seed on a later navigation.
 *
 * A same-tab click can't use this (bootstrap only runs on a full load); it seeds
 * localStorage from the link's onClick instead (seedCropSeasonsForSameTabNav).
 *
 * No-op when the param is absent (every load path other than the product-view
 * "View" link), so existing behavior is unchanged.
 */
const applyUrlCropSeasons = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  // Key off presence, not value: an empty `?seasonIds=` seeds nothing but must
  // still be stripped so it doesn't linger in the URL.
  if (!url.searchParams.has(SEASON_IDS_PARAM)) {
    return;
  }

  const seasonIds = (url.searchParams.get(SEASON_IDS_PARAM) ?? '')
    .split(',')
    .filter(Boolean);
  if (seasonIds.length) {
    localStorage.setItem(
      SELECTED_CROP_SEASONS_STORAGE_KEY,
      JSON.stringify(seasonIds)
    );
  }

  url.searchParams.delete(SEASON_IDS_PARAM);
  window.history.replaceState(
    window.history.state,
    '',
    `${url.pathname}${url.search}${url.hash}`
  );
};

export default applyUrlCropSeasons;
